'use client';
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export interface PhoneData {
  phone_country_code: string;
  phone_number: string;
}

interface PhoneInputFieldProps {
  countryCode?: string;
  value?: string;
  onChange?: (data: PhoneData) => void;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
}

export default function PhoneInputField({
  countryCode,
  value,
  onChange,
  label = 'Phone Number',
  required = false,
  error,
  className = '',
}: PhoneInputFieldProps) {
  const [phone, setPhone] = useState(value || '');

  useEffect(() => {
    if (value !== undefined) setPhone(value);
  }, [value]);

  const handleChange = (
    phoneValue: string,
    data: { dialCode: string; countryCode: string; name: string; format: string }
  ) => {
    setPhone(phoneValue);
    const numberWithoutCode = phoneValue.startsWith(data.dialCode)
      ? phoneValue.slice(data.dialCode.length)
      : phoneValue;
    onChange?.({
      phone_country_code: `+${data.dialCode}`,
      phone_number: numberWithoutCode,
    });
  };

  return (
    <div className={`phone-field-wrapper ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={`rounded-xl overflow-visible border transition-all focus-within:ring-2 focus-within:ring-ring ${error ? 'border-red-400' : 'border-input'}`}>
        <PhoneInput
          country={countryCode ? countryCode.toLowerCase() : 'in'}
          value={phone}
          onChange={handleChange}
          enableSearch
          searchPlaceholder="Search country..."
          preferredCountries={['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca']}
          inputStyle={{
            width: '100%',
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '14px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '52px',
            height: '42px',
            background: 'transparent',
            color: 'inherit',
          }}
          buttonStyle={{
            border: 'none',
            borderRight: '1px solid var(--border)',
            borderRadius: '0.75rem 0 0 0.75rem',
            background: 'transparent',
            width: '48px',
          }}
          containerStyle={{
            width: '100%',
          }}
          dropdownStyle={{
            width: '320px',
            maxHeight: '260px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            border: '1px solid var(--border)',
            zIndex: 9999,
            overflowY: 'auto',
          }}
          inputClass="!bg-transparent !text-foreground !border-none !outline-none"
          buttonClass="!bg-transparent hover:!bg-muted/50"
          searchClass="!bg-background !text-foreground !border-border"
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      <style jsx global>{`
        .phone-field-wrapper .react-tel-input {
          font-family: inherit;
        }
        .phone-field-wrapper .react-tel-input .form-control {
          background: transparent !important;
          color: inherit !important;
          font-family: inherit !important;
        }
        .phone-field-wrapper .react-tel-input .flag-dropdown {
          background: transparent !important;
          border: none !important;
        }
        .phone-field-wrapper .react-tel-input .selected-flag {
          background: transparent !important;
          padding: 0 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .phone-field-wrapper .react-tel-input .selected-flag:hover,
        .phone-field-wrapper .react-tel-input .selected-flag:focus {
          background: rgba(0,0,0,0.04) !important;
        }
        .phone-field-wrapper .react-tel-input .country-list {
          border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15) !important;
          border: 1px solid var(--border) !important;
          z-index: 9999 !important;
          background: var(--card) !important;
          color: var(--foreground) !important;
          width: 320px !important;
          max-height: 260px !important;
          overflow-y: auto !important;
        }
        .phone-field-wrapper .react-tel-input .country-list .country {
          padding: 8px 12px !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }
        .phone-field-wrapper .react-tel-input .country-list .country:hover,
        .phone-field-wrapper .react-tel-input .country-list .country.highlight {
          background: var(--muted) !important;
        }
        .phone-field-wrapper .react-tel-input .country-list .country .country-name {
          color: var(--foreground) !important;
          font-size: 13px !important;
        }
        .phone-field-wrapper .react-tel-input .country-list .country .dial-code {
          color: var(--muted-foreground) !important;
          font-size: 12px !important;
        }
        .phone-field-wrapper .react-tel-input .search-box {
          background: var(--background) !important;
          color: var(--foreground) !important;
          border: 1px solid var(--border) !important;
          border-radius: 8px !important;
          padding: 6px 10px !important;
          font-size: 13px !important;
          width: calc(100% - 16px) !important;
          margin: 8px !important;
        }
        .phone-field-wrapper .react-tel-input .search-box:focus {
          outline: none !important;
          border-color: var(--primary) !important;
        }
        .phone-field-wrapper .react-tel-input .flag {
          display: inline-block !important;
        }
      `}</style>
    </div>
  );
}
